import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNetwork } from '../contexts/NetworkContext';
import { useWallet } from '../contexts/WalletContext';
import { useContract } from '../contexts/ContractContext';
import Header from './Header';
import PolicyDescription from './PolicyDescription';
import ContractAddressInput from './ContractAddressInput';
import VaultUserFeatures from './VaultUserFeatures';
import VaultAdminFeatures from './VaultAdminFeatures';
import './HomePage.css';

const HomePage: React.FC = () => {
  const { currentNetwork } = useNetwork();
  const { isConnected, onAccountChange } = useWallet();
  const { isAddressSet, contractAddress } = useContract();
  
  // Get network from currentNetwork
  const displayNetwork = useMemo(() => {
    return currentNetwork;
  }, [currentNetwork]);
  
  // Basic state management
  const [loading] = useState(false);
  
  
  // Function to update header height CSS variable
  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector('header');
    if (header) {
      const height = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }, []);


  // Listen for account changes
  useEffect(() => {
    const unsubscribe = onAccountChange((newAccount) => {
      // Handle account changes if needed
      console.log('Account changed:', newAccount);
    });
    
    return unsubscribe;
  }, [onAccountChange]);

  // Update header height on component mount and when header changes
  useEffect(() => {
    // Initial update with a small delay to ensure header is rendered
    const initialTimeout = setTimeout(() => {
      updateHeaderHeight();
    }, 100);
    
    // Set up ResizeObserver to watch for header height changes
    const header = document.querySelector('header');
    if (header) {
      const resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      
      resizeObserver.observe(header);
      
      // Also listen for window resize events
      const handleResize = () => {
        updateHeaderHeight();
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        clearTimeout(initialTimeout);
        resizeObserver.disconnect();
        window.removeEventListener('resize', handleResize);
      };
    } else {
      return () => {
        clearTimeout(initialTimeout);
      };
    }
  }, [updateHeaderHeight]);

  return (
    <div className="home-page">
      <Header subtitle="Vault" />
      
      <main className="container">
        <div className="main-content">
            
            <PolicyDescription 
              currentNetwork={displayNetwork || undefined}
              contractAddress={contractAddress}
              isConnected={isConnected}
            />

            {!isConnected ? (
              <div className="connect-section">
                <h2>Connect Your Wallet</h2>
                <p>Please connect your wallet to interact with TrustLine-protected smart contracts.</p>
          </div>
            ) : !displayNetwork ? (
              <div className="no-network">
                <h3>No Network Selected</h3>
                <p>Please select a supported network to continue.</p>
        </div>
            ) : !isAddressSet ? (
              <ContractAddressInput />
            ) : (
              <>
                <VaultUserFeatures />
                <VaultAdminFeatures />
              </>
            )}
            
            <div className="loading" style={{ visibility: loading ? 'visible' : 'hidden' }}>
              Loading...
          </div>
        </div>
      </main>
      
      <footer>
        <p>Contact us at <a href="mailto:contact@trustline.id">contact@trustline.id</a></p>
      </footer>
    </div>
  );
};

export default HomePage;
